import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Avatar,
    InputAdornment,
    SvgIcon,
    useTheme,
    LinearProgress,
    CircularProgress,
    Alert,
    Button,
    TablePagination
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { getUserHoldings } from '../../../Utils/services';

const UserHoldings = () => {
    const [data, setData] = useState([]);
    const [loadSpinner, setLoadSpinner] = useState(false);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    async function getUserHoldingsFunction() {
        setLoadSpinner(true);
        setError(null);

        try {
            debugger;
            const params = {
                page: page + 1, // API expects 1-based index
                limit: rowsPerPage,
                search: searchTerm
            };

            const res = await getUserHoldings(params);
            if (res.data) {
                setData(res.data.data || []);
                setTotalRecords(res.data.total || 0);
            } else {
                setError('Invalid data format received from server');
            }
        } catch (err) {
            setError('Failed to load holdings data');
            console.error('Error fetching holdings:', err);
        } finally {
            setLoadSpinner(false);
        }
    }

    useEffect(() => {
        getUserHoldingsFunction();
    }, [page, rowsPerPage, searchTerm]); // Re-fetch when pagination or search changes

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page when changing rows per page
    };

    // Memoized calculations for local data (if needed)
    const { activeHoldings, soldHoldings, totalValue } = useMemo(() => {
        const safeData = Array.isArray(data) ? data : [];

        const active = safeData.filter(d => d.holding_status_id === 1).length;
        const sold = safeData.filter(d => d.holding_status_id === 3).length;
        const value = safeData.reduce((sum, curr) => sum + (curr.total_purchase_value || 0), 0);

        return {
            activeHoldings: active,
            soldHoldings: sold,
            totalValue: value
        };
    }, [data]);

    if (loadSpinner) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                    <Box sx={{ mt: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={getUserHoldingsFunction}
                        >
                            Retry
                        </Button>
                    </Box>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, minHeight: '100vh' }}>
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Summary Cards */}
                <Grid item xs={12} md={3}>
                    <Card elevation={3} sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Holdings
                            </Typography>
                            <Typography variant="h4" component="div">
                                {totalRecords}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card elevation={3} sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Active Investments
                            </Typography>
                            <Typography variant="h4" component="div" color="success.main">
                                {activeHoldings}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={totalRecords ? (activeHoldings / totalRecords) * 100 : 0}
                                color="success"
                                sx={{ mt: 1, height: 6 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card elevation={5} sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Sold Investments
                            </Typography>
                            <Typography variant="h4" component="div" color="error.main">
                                {soldHoldings}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={totalRecords ? (soldHoldings / totalRecords) * 100 : 0}
                                color="error"
                                sx={{ mt: 1, height: 6 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card elevation={3} sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Investment Value
                            </Typography>
                            <Typography variant="h4" component="div">
                                ${totalValue.toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card elevation={3}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">All Holdings</Typography>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search holdings..."
                                    value={searchTerm}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SvgIcon fontSize="small" color="action">
                                                    <SearchIcon />
                                                </SvgIcon>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setPage(0); // Reset to first page when searching
                                    }}
                                />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <TableContainer component={Paper} elevation={0}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Player</TableCell>
                                            <TableCell align="right">Stocks</TableCell>
                                            <TableCell align="right">Total Value</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Last Transaction</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.length > 0 ? (
                                            data.map((holding, index) => (
                                                <TableRow hover key={index}>
                                                    <TableCell>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                                                                {holding.customer_name?.charAt(0) || '?'}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2">{holding.customer_name || 'N/A'}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography fontWeight="medium">{holding.player_name || 'N/A'}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">{holding.number_of_stocks || 0}</TableCell>
                                                    <TableCell align="right">
                                                        <Typography fontWeight="medium">
                                                            ${(holding.total_purchase_value || 0).toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            (${(holding.average_purchase_price || 0).toFixed(2)} avg)
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={holding.holding_status_name || 'Unknown'}
                                                            size="small"
                                                            color={
                                                                holding.holding_status_id === 1 ? 'success' :
                                                                    holding.holding_status_id === 3 ? 'error' : 'default'
                                                            }
                                                            sx={{ width: 80 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {holding.latest_transaction_date ?
                                                            new Date(holding.latest_transaction_date).toLocaleDateString() : 'N/A'}
                                                        {holding.latest_transaction_date && (
                                                            <Typography variant="caption" display="block" color="textSecondary">
                                                                {new Date(holding.latest_transaction_date).toLocaleTimeString()}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    No holdings found
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={totalRecords}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserHoldings;